// Thư mục: src/stores/salesDonHangStore.js
import { defineStore } from "pinia";
// Import file service (chứa API)
import { DonHangApi } from "@/services/donHangService";
import NguoiDungApi from "@/services/userService";

export const useSalesDonHangStore = defineStore("salesDonHang", {
  state: () => ({
    // State cho danh sách
    isLoadingList: false,
    listError: null,
    items: [], // Đơn hàng của Sales
    page: 0,
    size: 10,
    sort: "ngayTao,desc",
    totalPages: 1,
    totalElements: 0,

    // Filter (Sales chỉ quan tâm các trạng thái này)
    filterTrangThai: null, // Mặc định

    // State cho Modal (Tạo mới / Chi tiết)
    isLoadingDetail: false,
    khoNhanVienOptions: [], // Danh sách Nhân viên Kho (Pickers)
    detailError: null,
    currentOrder: null, // Đơn hàng đang xem
  }),
  getters: {
    currentPage(state) {
      return state.page + 1;
    },
  },
  actions: {
    /**
     * Tải danh sách đơn hàng (chỉ của Sales này)
     */
    async fetchList() {
      this.isLoadingList = true;
      this.listError = null;
      try {
        const params = {
          page: this.page,
          size: this.size,
          sort: this.sort,
          trangThai: this.filterTrangThai,
        };
        // Gọi API listSales (Backend sẽ tự biết salesId qua Token)
        const data = await DonHangApi.listSales(params);
        this.items = data.content || [];
        this.totalPages = data.totalPages ?? 1;
        this.totalElements = data.totalElements ?? this.items.length;
        this.page = data.number ?? 0;
      } catch (e) {
        this.listError =
          e?.response?.data?.message || e?.message || "Lỗi tải danh sách";
      } finally {
        this.isLoadingList = false;
      }
    },

  async fetchListAll() {
      this.isLoadingList = true;
      this.listError = null;
      try {
        const params = {
          page: this.page,
          size: this.size,
          sort: this.sort,
          trangThai: this.filterTrangThai,
        };
        // Gọi API listSales (Backend sẽ tự biết salesId qua Token)
        const data = await DonHangApi.listSalesAll(params);
        this.items = data.content || [];
        this.totalPages = data.totalPages ?? 1;
        this.totalElements = data.totalElements ?? this.items.length;
        this.page = data.number ?? 0;
      } catch (e) {
        this.listError =
          e?.response?.data?.message || e?.message || "Lỗi tải danh sách";
      } finally {
        this.isLoadingList = false;
      }
    },

    // Đặt Filter (Bấm Tab)
    async setFilterTrangThaiAll(status) {
      this.filterTrangThai = status;
      this.page = 0;
      await this.fetchListAll();
    },
    async setFilterTrangThai(status) {
      this.filterTrangThai = status;
      this.page = 0;
      await this.fetchList();
    },
    setPage(p1) {
      this.page = Math.max(0, p1 - 1);
      this.fetchList();
    },
    setSize(s) {
      this.size = s;
      this.page = 0;
      this.fetchList();
    },

    // Mở Modal
    async openDetailModal(orderId) {
      this.isLoadingDetail = true;
      this.detailError = null;
      try {
        // Tải chi tiết đơn VÀ tải danh sách NV Kho
        const [orderData] = await Promise.all([
          DonHangApi.getById(orderId),
          this.fetchKhoOptionsIfNeeded(),
        ]);
        this.currentOrder = orderData;
      } catch (e) {
        this.detailError = e?.response?.data?.message || "Lỗi tải chi tiết";
      } finally {
        this.isLoadingDetail = false;
      }
    },
    async fetchKhoOptionsIfNeeded() {
      if (this.khoNhanVienOptions.length > 0) return;
      this.isLoadingOptions = true;
      try {
        // (Backend trả về List<NguoiDungResponse> { idNguoiDung, hoTen })
        const res = await NguoiDungApi.getUsersChucVuCode("NV_KHO");
        const data = res.data;
        this.khoNhanVienOptions = data || [];
      } catch (e) {
        console.error("Lỗi tải danh sách Nhân viên Kho:", e);
      } finally {
        this.isLoadingOptions = false;
      }
    },
    clearDetailModal() {
      this.currentOrder = null;
      this.detailError = null;
    },

    // --- Actions của Sales ---
    async createDonHang(payload) {
      this.isLoadingDetail = true;
      this.detailError = null;
      try {
        // Gọi API create (BE tự lấy salesId)
        await DonHangApi.create(payload);
        await this.fetchList(); // Tải lại list
      } catch (e) {
        this.detailError = e?.response?.data?.message || "Lỗi tạo đơn hàng";
        throw e;
      } finally {
        this.isLoadingDetail = false;
      }
    },
    async confirmShortage(orderId, payload) {
      this.isLoadingDetail = true;
      this.detailError = null;
      try {
        await DonHangApi.salesConfirm(orderId, payload);
        await this.fetchList();
      } catch (e) {
        this.detailError = e?.response?.data?.message || "Lỗi xác nhận";
        throw e;
      } finally {
        this.isLoadingDetail = false;
      }
    },
    async cancelOrder(orderId) {
      this.isLoadingDetail = true;
      this.detailError = null;
      try {
        await DonHangApi.salesCancel(orderId);
        // Tải lại chi tiết (để modal cập nhật) hoặc tải lại list
        await this.fetchList();
        this.clearDetailModal(); // Đóng modal
      } catch (e) {
        this.detailError = e?.response?.data?.message || "Lỗi hủy đơn";
        throw e;
      } finally {
        this.isLoadingDetail = false;
      }
    },

    async saveStopPlan(orderId, stops) {
      this.isLoadingDetail = true;
      this.detailError = null;
      try {
        // PUT /api/donhang/{id}/sales/plan-stops
        await DonHangApi.salesPlanStops(orderId, stops);
        await this.openDetailModal(orderId); // refresh chi tiết
        await this.fetchList(); // optional: refresh list
      } catch (e) {
        this.detailError =
          e?.response?.data?.message || "Lỗi lưu hoạch định điểm dừng";
        throw e;
      } finally {
        this.isLoadingDetail = false;
      }
    },

    async assignKhoUser(orderId, khoUserId) {
      this.isLoadingDetail = true;
      this.detailError = null;
      try {
        // PUT /api/donhang/{id}/sales/assign-kho?khoUserId=...
        await DonHangApi.assignKho(orderId, khoUserId ?? "");
        await this.openDetailModal(orderId);
        await this.fetchList(); // optional
      } catch (e) {
        this.detailError = e?.response?.data?.message || "Lỗi phân công NV Kho";
        throw e;
      } finally {
        this.isLoadingDetail = false;
      }
    },
  },
});
